import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRightOutlined } from "@ant-design/icons";

import Header from "./Header";
import AuctionSearchBar from "./AuctionSearchBar";

import backgroundImage from "../../assets/images/lomahuuto-hero-2026-2.webp";
import blackweekImage from "../../assets/images/blackweek-lomahuuto.webp";
import blackweekMobileImage from "../../assets/images/blackweek-mobile-lomahuuto.webp";
import { AuthContext } from "../../context/AuthContext";
import api from "../../services/Api";

/*
 * Heron uudistus 8.8.2026 (Mikan speksi): yksi selkeä pää-CTA, tehomyynti
 * tekstilinkkinä, lyhyt otsikko ilman huutomerkkejä, tumma gradientti tekstin
 * taakse, kompakti hakupalkki (max 900 px) kenttäotsikoin, luottamusrivi heron
 * alla. Kampanjapilleri: alle 1 000 € myyntihinta → 12 kk ilmoitus -50 %.
 */
const Hero = () => {
    const { isAuthenticated, login } = useContext(AuthContext);
    const navigate = useNavigate();
    const [scrollY, setScrollY] = useState(0);

    useEffect(() => {
        const handleScroll = () => {
            setScrollY(window.scrollY);
        };

        window.addEventListener("scroll", handleScroll, { passive: true });

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    const startDate = new Date("2024-11-22"); // Start date
    const endDate = new Date("2024-12-01"); // End date
    const currentDate = new Date();

    const isWithinRange = currentDate >= startDate && currentDate <= endDate;

    const ilmoitusPolku = isAuthenticated ? "/oma-sivu/ilmoitukset/uusi" : "/kirjaudu";
    /* Luonnostili: sama aloitus kuin Headerissa. */
    const aloitaMyynti = async (e) => {
        if (isAuthenticated) return;
        e.preventDefault();
        try {
            const vastaus = await api.aloitaLuonnos();
            await login(vastaus.token);
            navigate("/oma-sivu/ilmoitukset/uusi");
        } catch (err) {
            navigate("/kirjaudu");
        }
    };


    /* NÄKYVYYDEN HUOMIO (Mika 6.10.2026): myyjälle kohdistettu nosto, joka vie näkyvyyssivulle.
       Puhelimessa EI kelluvaa palloa — se peittäisi ilmoituksia ja sivulla on jo alapalkki —
       vaan sama paikka hero-osiossa molemmilla laitteilla. */
    /* BLACK WEEK (Mika 8.10.2026): näkyy vain kampanjaviikolla, 23.–30.11.2026 Suomen aikaa.
       Alku 22.11. klo 22 UTC = 23.11. klo 00 FI, loppu 30.11. klo 22 UTC = 1.12. klo 00 FI. */
    const blackWeek =
        Date.now() >= Date.UTC(2026, 10, 22, 22, 0, 0) &&
        Date.now() < Date.UTC(2026, 10, 30, 22, 0, 0);

    const blackWeekPalkki = blackWeek && (
        <Link
            to="/nakyvyys"
            className="block w-full bg-[#111110] text-[#FAC775] hover:text-white px-4 py-2.5 text-center text-[13px] sm:text-[15px]"
        >
            <span className="font-bold tracking-[0.08em]">BLACK WEEK</span>
            <span className="mx-2 hidden sm:inline">·</span>
            <span className="block sm:inline">Ilmoitus −50 %, näkyvyys −40 %, Teho1 149,90 €</span>
            <span className="mx-2 hidden sm:inline">·</span>
            <span className="block sm:inline text-white/70">päättyy su 30.11. klo 23.59</span>
            <span className="ml-3 hidden sm:inline-block rounded-lg bg-[#EF9F27] px-3 py-1 text-[12px] font-bold text-[#412402]">
                Katso tarjoukset →
            </span>
        </Link>
    );

    const nakyvyysPallo = (
        <Link
            to="/nakyvyys"
            aria-label="Lisää näkyvyyttä ilmoituksellesi"
            className={"fixed bottom-[84px] right-3 sm:bottom-6 sm:right-6 z-40 flex h-[86px] w-[86px] sm:h-32 sm:w-32 items-center justify-center rounded-full shadow-xl ring-2 ring-white/80 transition-all text-center " + (blackWeek ? "bg-[#111110] text-[#FAC775] hover:text-white hover:bg-black ring-[#EF9F27]" : "bg-amber-500 text-white hover:text-white hover:bg-amber-600")}
        >
            <span className="px-1.5 text-[10px] sm:text-[13px] font-bold leading-tight">
                {blackWeek ? (
                    <>
                        BLACK
                        <br />
                        WEEK
                        <br />
                        −50 %
                    </>
                ) : (
                    <>
                        Lisää
                        <br />
                        näkyvyyttä
                        <br />
                        ilmoituksellesi →
                    </>
                )}
            </span>
        </Link>
    );

    const kampanjaPilleri = (
        <Link
            to={ilmoitusPolku} onClick={aloitaMyynti}
            className="mt-4 inline-block bg-amber-500/95 hover:bg-amber-500 text-white hover:text-white text-[13.5px] font-medium px-4 py-1.5 rounded-xl transition-all"
        >
            Myyntihinta alle 1 000 € → 12 kk ilmoitus -50 %
        </Link>
    );

    /* OSTAJAN POLKU JA SISÄINEN LINKITYS (Mika 5.10.2026): etusivulta puuttui linkki
       myytäviin ja vuokrattaviin lomaosakkeisiin. Ostaja joutui etsimään ne valikosta, ja
       Googlelle paras sivumme jäi ilman linkkiä etusivulta. */
    const selauslinkit = (
        <div className="mt-3 flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <Link
                to="/myydaan-lomaosake"
                className="inline-block text-white/90 hover:text-white text-[14px] underline underline-offset-4"
            >
                Katso myytävät lomaosakkeet <ArrowRightOutlined className="ml-1 text-xs" />
            </Link>
            <Link
                to="/vuokrataan-lomaosake"
                className="inline-block text-white/90 hover:text-white text-[14px] underline underline-offset-4"
            >
                Vuokrattavat lomaosakkeet <ArrowRightOutlined className="ml-1 text-xs" />
            </Link>
        </div>
    );

    const tehomyyntiLinkki = (
        <a
            href="https://tehomyynti.lomahuuto.fi/"
            className="mt-3 inline-block text-white/90 hover:text-white text-[14px] underline underline-offset-4"
        >
            Tutustu tehomyyntikampanjaan <ArrowRightOutlined className="ml-1 text-xs" />
        </a>
    );

    return (
        <>
            <Header pageType={0} />
            {blackWeekPalkki}
            <div className="sm:hidden">
                <div className="relative overflow-hidden">
                    <img
                        src={backgroundImage}
                        alt="Lomahuuto hero background"
                        className="absolute inset-0 w-full h-full object-cover object-center"
                        fetchpriority="high"
                    />
                    {/* Tummennus vasemmalta/keskeltä, jotta teksti erottuu */}
                    <div
                        className="absolute inset-0"
                        style={{ background: "linear-gradient(90deg, rgba(0,0,0,.45) 0%, rgba(0,0,0,.3) 55%, rgba(0,0,0,.18) 100%)" }}
                    />
                    <div className="relative flex flex-col items-center text-center px-5 pt-8 pb-12">
                        {isWithinRange && (
                            <img src={blackweekMobileImage} alt="Black Week" className="w-full mb-4" />
                        )}
                        <h1 className="text-2xl font-semibold text-white mb-2">
                            Lomaosakkeen myynti ja vuokraus helposti
                        </h1>
                        <p className="text-white/90 text-[15px] mb-5">
                            Suomen lomaosakkeiden markkinapaikka. Luo ilmoitus valmiista pohjasta muutamassa minuutissa.
                        </p>
                        <Link
                            to={ilmoitusPolku} onClick={aloitaMyynti}
                            className="py-2.5 px-7 inline-block bg-primary text-white hover:text-white text-lg rounded-2xl hover:bg-primary-50 transition-all cursor-pointer"
                        >
                            Myy tai vuokraa lomaosakkeesi
                        </Link>
                        {selauslinkit}
                        {tehomyyntiLinkki}
                        {kampanjaPilleri}
                    </div>
                </div>
            </div>
            <div className="hidden sm:block">
                <div className="relative overflow-hidden">
                    <img
                        src={backgroundImage}
                        alt="Lomahuuto hero background"
                        className="absolute inset-0 w-full h-full object-cover object-center"
                        style={{ transform: `translateY(${scrollY * 0.5}px)` }}
                        fetchpriority="high"
                    />
                    <div
                        className="absolute inset-0"
                        style={{ background: "linear-gradient(90deg, rgba(0,0,0,.45) 0%, rgba(0,0,0,.3) 50%, rgba(0,0,0,.15) 100%)" }}
                    />
                    <div className="relative flex flex-col max-w-screen-2xl m-auto items-center text-center pt-16 pb-20 px-5">
                        {isWithinRange && (
                            <img src={blackweekImage} alt="Black Week" className="w-full mb-6" />
                        )}
                        <h1 className="text-3xl md:text-4xl font-semibold text-white mb-3">
                            Lomaosakkeen myynti ja vuokraus helposti
                        </h1>
                        <p className="text-white/90 text-lg mb-7 max-w-2xl">
                            Suomen lomaosakkeiden markkinapaikka. Luo ilmoitus valmiista pohjasta muutamassa minuutissa.
                        </p>
                        <Link
                            to={ilmoitusPolku} onClick={aloitaMyynti}
                            className="py-2.5 px-8 inline-block bg-primary text-white hover:text-white text-lg rounded-2xl hover:bg-primary-50 transition-all cursor-pointer"
                        >
                            Myy tai vuokraa lomaosakkeesi
                        </Link>
                        {selauslinkit}
                        {tehomyyntiLinkki}
                        {kampanjaPilleri}
                    </div>
                </div>
            </div>
            {/* Pikahaku limittäin heron alareunan päälle (Mikan mockup 8.8.2026) */}
            <div className="relative z-20 -mt-5 md:-mt-11 px-4">
                <div className="max-w-[1080px] mx-auto">
                    <AuctionSearchBar />
                </div>
            </div>
            <div className="sm:hidden p-6 w-full text-center">
                <Link
                    to={"/kohteet"}
                    className="bg-primary text-white text-lg px-6 py-2 rounded-2xl hover:bg-primary-50 hover:text-white transition-all"
                >
                    Katso kaikki ilmoitukset
                    <ArrowRightOutlined className="ml-2 text-base" />
                </Link>
            </div>
            {nakyvyysPallo}
        </>
    );
};

export default Hero;
